import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Certificate, Eye } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/DashboardLayout";
import { PageHeader, CategoryBadge } from "../components/ui-bits";
import { PassportDetailModal } from "../components/PassportDetailModal";
import { api, getMerchantId, formatDate, scoreColor, CATEGORY_LABELS } from "../lib/api";

const ScorePill = ({ score }) => (
  <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: scoreColor(score) }}>
    <span className="h-2 w-2 rounded-full" style={{ background: scoreColor(score) }} /> {score}
  </span>
);

export default function Passports() {
  const merchantId = getMerchantId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/passports?merchant_id=${merchantId}`);
        setRows(data.passports);
      } catch { toast.error("Failed to load passports"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <DashboardLayout>
      <PageHeader overline="Issued Certificates" title="Passports" subtitle="Every Digital Product Passport you have issued." />

      {loading ? (
        <div className="h-64 rounded-2xl bg-white/60 border border-navy/5 animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-navy/10 p-12 text-center text-navy/50">
          <Certificate size={40} weight="light" className="mx-auto text-navy/20" />
          <p className="mt-4 text-sm">No passports issued yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-navy/10 shadow-luxe overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="passports-table">
              <thead>
                <tr className="text-left text-[0.62rem] uppercase tracking-[0.15em] text-navy/40 border-b border-navy/10">
                  <th className="px-6 py-4 font-normal">Certificate</th>
                  <th className="px-6 py-4 font-normal">Product</th>
                  <th className="px-6 py-4 font-normal">Issued</th>
                  <th className="px-6 py-4 font-normal">Provenance</th>
                  <th className="px-6 py-4 font-normal">Status</th>
                  <th className="px-6 py-4 font-normal text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.certificateId} className="border-b border-navy/5 last:border-0 hover:bg-cream/50 transition-colors">
                    <td className="px-6 py-4 text-navy/70 font-mono text-xs">{r.certificateId}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-cream overflow-hidden shrink-0">
                          {r.image ? <img src={r.image} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div>
                          <p className="text-navy">{r.productName}</p>
                          <CategoryBadge label={CATEGORY_LABELS[r.category] || r.category} />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-navy/60">{formatDate(r.issuedAt)}</td>
                    <td className="px-6 py-4"><ScorePill score={r.provenanceScore} /></td>
                    <td className="px-6 py-4"><span className="text-[0.65rem] uppercase tracking-wide bg-score-green/10 text-score-green rounded-full px-2.5 py-1">{r.status}</span></td>
                    <td className="px-6 py-4 text-right">
                      <button data-testid="view-passport-button" onClick={() => setDetail(r)} className="inline-flex items-center gap-1.5 text-navy hover:text-gold transition-colors text-xs">
                        <Eye size={16} weight="light" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <PassportDetailModal detail={detail} merchantId={merchantId} onClose={() => setDetail(null)} />
      )}
    </DashboardLayout>
  );
}
